/**
 * One customer's permission defaults — what each ROLE may do in their tenant.
 *
 * TEMPORARY. This grid was Settings → Permissions, editable by the org's owner.
 * Permission management is platform-administered for now; see
 * TODO(permissions-relocation) at `GET`/`PUT
 * /api/admin/tenants/:tenantId/capability-grants`, which is also where the
 * removal condition lives. Its org-side routes were deleted rather than
 * re-gated.
 *
 * ── The empty response means DEFAULTS, not "nothing allowed" ────────────
 *
 * The endpoint returns OVERRIDES only, so an unconfigured tenant answers with an
 * empty list and every cell falls back to `CAPABILITIES[].defaults`. That is
 * also what makes the RLS trap behind this screen so quiet: a scoped read would
 * return no rows and draw exactly this same complete, plausible grid — of code
 * defaults, for a customer who has overridden half of them. Both routes read
 * through `getUnscopedDb()`; the mutation-checked test is what keeps that true.
 *
 * ── Saving per cell, not behind the page's Save bar ─────────────────────
 *
 * Same call `ChatCostVisibilityPicker` makes beside it: the staged-edit rule on
 * this tab is for money VALUES, where a mistyped digit plus a stray click moves
 * a real limit. These are enumerated levels with nothing to mistype.
 *
 * What DID change in the move: a failed write now says so. The org-side version
 * silently refetched and let the cell snap back, which reads as a mis-click —
 * tolerable when you are editing your own org, not when an operator is changing
 * someone else's permissions and would otherwise believe it landed.
 */
import { useCallback, useEffect, useState } from 'react'
import { useToast } from '@papan/core-ui/ui'
import {
  CAPABILITIES, CAP_ROLES, CAP_SECTIONS, CAP_VALUE_OPTIONS, capValueLabel,
  type CapValue, type CapDef,
} from '@papan/types'
import { budgetApi } from '../../client/budget'

const toStr = (v: CapValue): string => (typeof v === 'boolean' ? (v ? 'true' : 'false') : v)

const valColor = (v: string): string =>
  (v === 'true' || v === 'write' || v === 'top') ? 'text-emerald-400'
    : (v === 'read' || v === 'mid') ? 'text-blue-400'
      : 'themed-text-muted'

export function CustomerPermissions({ tenantId, canEdit }: { tenantId: string; canEdit: boolean }) {
  const toast = useToast()
  const [overrides, setOverrides] = useState<Record<string, Record<string, string>>>({})
  const [loading, setLoading] = useState(true)

  const load = useCallback(() => {
    return budgetApi.getCapabilityGrants(tenantId).then((d) => {
      const m: Record<string, Record<string, string>> = {}
      for (const g of d.grants) (m[g.capability] ??= {})[g.role] = g.level
      setOverrides(m)
    })
  }, [tenantId])

  useEffect(() => {
    let live = true
    setLoading(true)
    load()
      .catch(() => { if (live) toast.error('Could not load this tenant’s permission defaults') })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [load, toast])

  const defStr = (cap: CapDef, role: string): string => toStr((cap.defaults as Record<string, CapValue>)[role])
  const effective = (cap: CapDef, role: string): string => overrides[cap.id]?.[role] ?? defStr(cap, role)
  const overridden = (cap: CapDef, role: string): boolean => overrides[cap.id]?.[role] !== undefined

  async function change(cap: CapDef, role: string, newStr: string) {
    const reset = newStr === defStr(cap, role)
    setOverrides((prev) => {
      const next = { ...prev, [cap.id]: { ...(prev[cap.id] || {}) } }
      if (reset) { delete next[cap.id][role]; if (!Object.keys(next[cap.id]).length) delete next[cap.id] }
      else next[cap.id][role] = newStr
      return next
    })
    try {
      await budgetApi.setCapabilityGrant(tenantId, cap.id, role, reset ? null : newStr)
    } catch (err) {
      toast.error(`Could not save ${cap.label} for ${role}: ${(err as Error).message}`)
      load().catch(() => {}) // back to server truth — the optimistic cell was a guess
    }
  }

  if (loading) return <div className="themed-text-muted text-sm p-3">Loading permission defaults…</div>

  return (
    <div className="themed-bg-secondary rounded-lg p-3">
      <p className="themed-text-muted text-[11px] mb-3">
        <span className="text-emerald-400">write / top / yes</span> ·{' '}
        <span className="text-blue-400">read / mid</span> ·{' '}
        <span className="themed-text-muted">none / low / no</span>.{' '}
        <span className="text-purple-400">●</span> = changed from the built-in default.{' '}
        <b className="themed-text">Owner is always-allow</b>, so it cannot be lowered.
      </p>
      {/* The grid is six role columns wide and this column is narrower than the
          settings page it came from — so it scrolls inside its own box rather
          than pushing the page sideways. */}
      <div className="overflow-x-auto">
        {CAP_SECTIONS.map((section) => {
          const caps = CAPABILITIES.filter((c) => c.section === section)
          if (!caps.length) return null
          return (
            <div key={section} className="mb-5 min-w-[540px]">
              <h4 className="themed-text text-xs font-semibold mb-1">{section}</h4>
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="themed-text-muted text-[11px]">
                    <th className="text-left font-medium py-1 pr-3 w-[40%]">Capability</th>
                    {CAP_ROLES.map((r) => (
                      <th key={r.id} className="text-center font-medium py-1 px-2" title={r.hint}>{r.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {caps.map((cap) => (
                    <tr key={cap.id} className="border-t themed-border">
                      {/* NAME first, identifier second: the id is what appears in
                          `capability_grants`, in API payloads and in support
                          conversations, so it stays — demoted, not dropped. */}
                      <td className="py-2 pr-3 align-top">
                        <div className="themed-text text-xs font-medium">{cap.label}</div>
                        <div className="themed-text-muted font-mono text-[11px] mt-0.5">
                          {cap.id}
                          {cap.scope === 'folder' ? <span> · per-folder</span>
                            : cap.scope === 'team' ? <span> · per-team</span>
                              : cap.scope === 'keybook' ? <span> · per-collection/key</span> : null}
                        </div>
                        {cap.note ? <div className="themed-text-muted text-xs mt-0.5">{cap.note}</div> : null}
                      </td>
                      {CAP_ROLES.map((r) => {
                        const val = effective(cap, r.id)
                        const editable = canEdit && r.id !== 'owner'
                        return (
                          <td key={r.id} className="text-center py-1.5 px-2">
                            {editable ? (
                              <span className="inline-flex items-center gap-1 justify-center">
                                <select
                                  value={val}
                                  onChange={(e) => change(cap, r.id, e.target.value)}
                                  className={`themed-input text-xs font-mono rounded px-1 py-0.5 ${valColor(val)}`}
                                >
                                  {CAP_VALUE_OPTIONS[cap.values].map((opt) => {
                                    const s = toStr(opt)
                                    return <option key={s} value={s}>{capValueLabel(opt)}</option>
                                  })}
                                </select>
                                {overridden(cap, r.id) ? <span className="text-purple-400 text-[10px]" title="overridden">●</span> : null}
                              </span>
                            ) : (
                              <span className={`font-mono text-xs ${valColor(val)}`}>
                                {capValueLabel(cap.values === 'bool' ? val === 'true' : (val as CapValue))}
                              </span>
                            )}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        })}
      </div>
    </div>
  )
}
